import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  Typography,
  Button,
  Box,
  Stack,
  Fade,
  useTheme,
} from "@mui/material";
import { styled, keyframes } from "@mui/material/styles";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import useAuth from "src/hooks/useAuth";
import Iconify from "src/components/Iconify";
import markWhatsAppGroupJoined from "src/api/user/whatsapp/markJoined";
import getWhatsAppGroupJoinedStatus from "src/api/user/whatsapp/getJoinedStatus";

const WHATSAPP_GROUP_LINK =
  "https://chat.whatsapp.com/IoyA3yb78NUDe11RLzOgcg?s=cl&p=i&mlu=0&ilr=4";

const TRANSLATIONS = {
  en: {
    badge: "Official Community",
    title: "Join Our WhatsApp Group",
    subtitle: "Stay updated with real-time news and community support.",
    feature1: "Real-Time Announcements",
    feature2: "Active Member Network",
    feature3: "24/7 Priority Support",
    joinBtn: "Join WhatsApp Group",
    alreadyJoinedBtn: "I've Already Joined",
    alreadyJoinedSuccess: "Already joined",
  },
  fr: {
    badge: "Communauté Officielle",
    title: "Rejoignez Notre Groupe WhatsApp",
    subtitle: "Recevez les actualités en temps réel et le support de la communauté.",
    feature1: "Annonces en temps réel",
    feature2: "Réseau de membres actifs",
    feature3: "Support prioritaire 24/7",
    joinBtn: "Rejoindre le groupe WhatsApp",
    alreadyJoinedBtn: "J'ai déjà rejoint le groupe",
    alreadyJoinedSuccess: "Déjà rejoint",
  },
};

// Keyframe animations
const pulseGlow = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(37, 211, 102, 0.7);
  }
  70% {
    box-shadow: 0 0 0 14px rgba(37, 211, 102, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(37, 211, 102, 0);
  }
`;

const floatAnim = keyframes`
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-3px);
  }
`;

// Styled Components
const DialogCard = styled(Dialog)(({ theme }) => ({
  "& .MuiBackdrop-root": {
    backdropFilter: "blur(6px)",
    backgroundColor: "rgba(10, 16, 20, 0.75)",
  },
  "& .MuiDialog-container": {
    alignItems: "center",
    justifyContent: "center",
  },
  "& .MuiPaper-root": {
    borderRadius: "22px",
    maxWidth: "400px",
    width: "100%",
    margin: "12px",
    maxHeight: "calc(100vh - 24px)",
    overflow: "hidden",
    boxShadow:
      theme.palette.mode === "dark"
        ? "0 20px 50px -12px rgba(0, 0, 0, 0.85), 0 0 35px rgba(37, 211, 102, 0.2)"
        : "0 20px 50px -12px rgba(18, 140, 126, 0.28), 0 0 30px rgba(37, 211, 102, 0.16)",
    border:
      theme.palette.mode === "dark"
        ? "1px solid rgba(37, 211, 102, 0.25)"
        : "1px solid rgba(37, 211, 102, 0.2)",
    background:
      theme.palette.mode === "dark"
        ? "linear-gradient(180deg, #182226 0%, #11181c 100%)"
        : "linear-gradient(180deg, #ffffff 0%, #f9fcfa 100%)",
  },
}));

const HeaderBanner = styled(Box)(() => ({
  position: "relative",
  height: "85px",
  background: "linear-gradient(135deg, #075E54 0%, #128C7E 55%, #25D366 100%)",
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "center",
  padding: "12px 16px",
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    top: "-40px",
    right: "-40px",
    width: "140px",
    height: "140px",
    borderRadius: "50%",
    background: "radial-gradient(circle, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0) 70%)",
  },
  "&::after": {
    content: '""',
    position: "absolute",
    bottom: "-30px",
    left: "-20px",
    width: "110px",
    height: "110px",
    borderRadius: "50%",
    background: "radial-gradient(circle, rgba(37,211,102,0.4) 0%, rgba(37,211,102,0) 70%)",
  },
}));

const LogoWrapper = styled(Box)(() => ({
  position: "relative",
  margin: "-34px auto 0 auto",
  width: "68px",
  height: "68px",
  borderRadius: "50%",
  padding: "4px",
  background: "linear-gradient(135deg, #25D366 0%, #128C7E 100%)",
  animation: `${pulseGlow} 2.5s infinite`,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 2,
}));

const LogoInner = styled(Box)(({ theme }) => ({
  width: "100%",
  height: "100%",
  borderRadius: "50%",
  backgroundColor: theme.palette.mode === "dark" ? "#141e22" : "#ffffff",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
  animation: `${floatAnim} 3s ease-in-out infinite`,
}));

// Vector SVG WhatsApp Logo
const WhatsAppLogoSvg = ({ size = 42 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M12.004 2C6.48 2 2 6.48 2 12C2 13.76 2.46 15.41 3.26 16.85L2.05 21.36C1.98 21.62 2.05 21.89 2.24 22.08C2.39 22.23 2.6 22.31 2.82 22.31C2.9 22.31 2.99 22.29 3.07 22.26L7.75 21.01C9.07 21.73 10.49 22.11 12.004 22.11C17.524 22.11 22.004 17.63 22.004 12.11C22.004 6.59 17.524 2 12.004 2Z"
      fill="#25D366"
    />
    <path
      d="M17.47 15.02C17.2 14.88 15.89 14.24 15.65 14.15C15.41 14.06 15.23 14.02 15.05 14.28C14.88 14.54 14.38 15.15 14.22 15.33C14.07 15.5 13.92 15.53 13.65 15.39C13.38 15.26 12.52 14.97 11.51 14.07C10.72 13.36 10.18 12.49 10.02 12.22C9.87 11.96 10 11.81 10.15 11.67C10.27 11.55 10.42 11.35 10.56 11.2C10.69 11.05 10.74 10.93 10.83 10.76C10.91 10.58 10.87 10.43 10.81 10.3C10.74 10.16 10.23 8.93 10.02 8.42C9.84 7.98 9.66 7.99 9.49 7.98C9.33 7.97 9.15 7.97 8.97 7.97C8.79 7.97 8.5 8.04 8.26 8.3C8.02 8.56 7.35 9.19 7.35 10.47C7.35 11.75 8.28 12.98 8.42 13.15C8.55 13.32 10.24 15.93 12.83 17.05C14.98 17.98 15.42 17.8 15.89 17.75C16.36 17.71 17.4 17.13 17.62 16.52C17.84 15.91 17.84 15.38 17.77 15.27C17.71 15.15 17.54 15.08 17.27 15.02H17.47Z"
      fill="white"
    />
  </svg>
);

const FeatureItem = ({ icon, text }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1.25,
      py: 0.65,
      px: 1.25,
      borderRadius: "10px",
      bgcolor: (theme) =>
        theme.palette.mode === "dark"
          ? "rgba(37, 211, 102, 0.08)"
          : "rgba(37, 211, 102, 0.06)",
      border: (theme) =>
        theme.palette.mode === "dark"
          ? "1px solid rgba(37, 211, 102, 0.14)"
          : "1px solid rgba(37, 211, 102, 0.12)",
    }}
  >
    <Box
      sx={{
        width: 24,
        height: 24,
        borderRadius: "7px",
        bgcolor: "#25D366",
        color: "#ffffff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        boxShadow: "0 2px 6px rgba(37, 211, 102, 0.35)",
      }}
    >
      <Iconify icon={icon} width={14} height={14} />
    </Box>
    <Typography
      variant="body2"
      sx={{
        fontWeight: 600,
        fontSize: { xs: "0.78rem", sm: "0.82rem" },
        color: "text.primary",
        textAlign: "left",
        lineHeight: 1.2,
      }}
    >
      {text}
    </Typography>
  </Box>
);

export default function WhatsAppJoinPopup() {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { user, isAdmin, isSubAdmin } = useAuth();
  const location = useLocation();
  const theme = useTheme();
  const { i18n } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const isFr = (
    i18n?.language ||
    user?.language ||
    localStorage.getItem("i18nextLng") ||
    ""
  )
    .toLowerCase()
    .startsWith("fr");

  const content = isFr ? TRANSLATIONS.fr : TRANSLATIONS.en;

  // Storage check helper.
  // IMPORTANT: this must be keyed per-user, never global. A shared/public
  // browser can be used by multiple accounts, and a global flag would leak
  // one user's "joined" confirmation to every other account on that device
  // (including brand-new registrations that never confirmed anything).
  const hasUserJoined = React.useCallback(() => {
    try {
      const identifier = user?.id ?? user?.username;
      if (!identifier) return false;
      return localStorage.getItem(`whatsapp_group_joined_${identifier}`) === "true";
    } catch {
      return false;
    }
  }, [user?.id, user?.username]);

  const markJoinedLocally = React.useCallback(() => {
    try {
      const identifier = user?.id ?? user?.username;
      if (identifier) {
        localStorage.setItem(`whatsapp_group_joined_${identifier}`, "true");
      }
    } catch (e) {
      console.error(e);
    }
  }, [user?.id, user?.username]);

  useEffect(() => {
    // One-time cleanup of the old global (non-user-specific) flags from an
    // earlier version of this component. Those leaked one user's "joined"
    // confirmation to every other account on the same browser.
    try {
      localStorage.removeItem("whatsapp_group_joined");
      localStorage.removeItem("whatsapp_joined");
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    // Only show to regular registered users (new or existing)
    const isSuperAdmin =
      isAdmin ||
      isSubAdmin ||
      JSON.parse(localStorage.getItem("isAdmin") || "false") ||
      JSON.parse(localStorage.getItem("isSubAdmin") || "false") ||
      user?.is_super_admin ||
      user?.is_sub_admin;

    if (isSuperAdmin) {
      setOpen(false);
      return undefined;
    }

    // Do not show on admin routes
    if (location.pathname.startsWith("/admin")) {
      setOpen(false);
      return undefined;
    }

    if (!user) {
      return undefined;
    }

    let cancelled = false;
    let timer;

    // The server (users.whatsapp_group_joined) is the source of truth, not
    // localStorage — localStorage is per-browser and gets wiped by the user,
    // so it must never be the only thing gating this popup.
    (async () => {
      let alreadyJoined = false;

      try {
        const { data } = await getWhatsAppGroupJoinedStatus();
        alreadyJoined = !!data?.joined;
        if (alreadyJoined) {
          markJoinedLocally();
        }
      } catch (e) {
        // If the status check fails (offline, etc.), fall back to the local
        // cache rather than annoying an already-joined user again.
        alreadyJoined = hasUserJoined();
      }

      if (cancelled) return;

      if (alreadyJoined) {
        setOpen(false);
        return;
      }

      // Smooth delay before showing popup
      timer = setTimeout(() => {
        if (!cancelled) setOpen(true);
      }, 1200);
    })();

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [user, location.pathname, isAdmin, isSubAdmin, hasUserJoined, markJoinedLocally]);

  const handleJoinClick = () => {
    // Just open the WhatsApp group invite in a new tab. The popup keeps
    // showing on future visits until the user confirms via the button below,
    // since opening the link doesn't guarantee they actually joined.
    window.open(WHATSAPP_GROUP_LINK, "_blank", "noopener,noreferrer");
  };

  const handleAlreadyJoinedClick = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await markWhatsAppGroupJoined();
      markJoinedLocally();
      setOpen(false);
      enqueueSnackbar(content.alreadyJoinedSuccess, { variant: "success" });
    } catch (err) {
      enqueueSnackbar(
        err?.message || "Something went wrong. Please try again.",
        { variant: "error" }
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <DialogCard
      open={open}
      disableEscapeKeyDown
      onClose={() => {}}
      TransitionComponent={Fade}
      transitionDuration={350}
    >
      {/* Top Gradient Banner */}
      <HeaderBanner sx={{ justifyContent: "center" }}>
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.75,
            px: 1.5,
            py: 0.4,
            borderRadius: "20px",
            bgcolor: "rgba(255, 255, 255, 0.2)",
            backdropFilter: "blur(4px)",
            border: "1px solid rgba(255, 255, 255, 0.35)",
            zIndex: 1,
          }}
        >
          <Box
            sx={{
              width: 6,
              height: 6,
              borderRadius: "50%",
              bgcolor: "#ffffff",
              boxShadow: "0 0 6px #ffffff",
            }}
          />
          <Typography
            variant="caption"
            sx={{
              color: "#ffffff",
              fontWeight: 800,
              letterSpacing: 0.5,
              textTransform: "uppercase",
              fontSize: "0.68rem",
            }}
          >
            {content.badge}
          </Typography>
        </Box>
      </HeaderBanner>

      {/* Floating Center WhatsApp Badge */}
      <LogoWrapper>
        <LogoInner>
          <WhatsAppLogoSvg size={42} />
        </LogoInner>
      </LogoWrapper>

      {/* Dialog Body Content */}
      <DialogContent sx={{ px: { xs: 2, sm: 2.75 }, py: 1.75, textAlign: "center" }}>
        <Typography
          variant="h6"
          component="h2"
          sx={{
            fontWeight: 800,
            fontSize: { xs: "1.15rem", sm: "1.28rem" },
            mb: 0.4,
            letterSpacing: "-0.3px",
            background:
              theme.palette.mode === "dark"
                ? "linear-gradient(135deg, #ffffff 30%, #25D366 100%)"
                : "linear-gradient(135deg, #111827 40%, #128C7E 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          {content.title}
        </Typography>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            mb: 1.5,
            px: 0.5,
            fontSize: { xs: "0.78rem", sm: "0.82rem" },
            lineHeight: 1.35,
          }}
        >
          {content.subtitle}
        </Typography>

        {/* Simplified Highlights */}
        <Stack spacing={0.75} sx={{ mb: 1.75 }}>
          <FeatureItem
            icon="heroicons-outline:speakerphone"
            text={content.feature1}
          />
          <FeatureItem
            icon="heroicons-outline:user-group"
            text={content.feature2}
          />
          <FeatureItem
            icon="heroicons-outline:chat-alt-2"
            text={content.feature3}
          />
        </Stack>

        {/* Action Buttons */}
        <Stack spacing={0.9}>
          {/* Main Join CTA Button */}
          <Button
            fullWidth
            size="medium"
            variant="contained"
            onClick={handleJoinClick}
            startIcon={
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Iconify icon="ri:whatsapp-fill" width={20} height={20} />
              </Box>
            }
            endIcon={<Iconify icon="eva:external-link-fill" width={16} height={16} />}
            sx={{
              py: 1.05,
              fontSize: { xs: "0.86rem", sm: "0.9rem" },
              fontWeight: 800,
              borderRadius: "14px",
              background: "linear-gradient(135deg, #25D366 0%, #128C7E 100%)",
              color: "#ffffff",
              boxShadow: "0 6px 18px rgba(37, 211, 102, 0.4)",
              letterSpacing: "0.1px",
              textTransform: "none",
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                background: "linear-gradient(135deg, #28e06d 0%, #0e786b 100%)",
                boxShadow: "0 8px 22px rgba(37, 211, 102, 0.5)",
                transform: "translateY(-1px)",
              },
            }}
          >
            {content.joinBtn}
          </Button>

          {/* Already Joined Button */}
          <Button
            fullWidth
            size="small"
            variant="outlined"
            onClick={handleAlreadyJoinedClick}
            disabled={submitting}
            startIcon={<Iconify icon="eva:checkmark-circle-2-outline" width={16} height={16} />}
            sx={{
              py: 0.7,
              borderRadius: "12px",
              borderColor: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.15)"
                  : "rgba(0, 0, 0, 0.12)",
              color: "text.primary",
              fontWeight: 600,
              fontSize: "0.78rem",
              textTransform: "none",
              "&:hover": {
                borderColor: "#25D366",
                bgcolor: (theme) =>
                  theme.palette.mode === "dark"
                    ? "rgba(37, 211, 102, 0.08)"
                    : "rgba(37, 211, 102, 0.06)",
              },
            }}
          >
            {submitting ? "..." : content.alreadyJoinedBtn}
          </Button>
        </Stack>
      </DialogContent>
    </DialogCard>
  );
}
